import { UseFormReturnType } from '@mantine/form'

import { CrFormStepsState, ReporterFeedback } from '@/types'

export enum CrFormStep {
  PREPARATION = 'preparation',
  ATTENDANCE = 'attendance',
  OPENING = 'opening',
  QUESTIONING = 'questioning',
  CLOSING = 'closing',
}

// Shown once per event.
export const EVENT_LEVEL_STEPS = [CrFormStep.PREPARATION, CrFormStep.ATTENDANCE]
// Repeated per witness; keyed by witness index so expand/done is tracked per witness.
export const PER_WITNESS_STEPS = [
  CrFormStep.OPENING,
  CrFormStep.QUESTIONING,
  CrFormStep.CLOSING,
]

// e.g. "opening:0" — a per-witness step's state key.
export const witnessStepKey = (
  step: CrFormStep,
  witnessIndex: number,
): string => `${step}:${witnessIndex}`

export const DEFAULT_STEP_STATE = { expanded: true, done: false }

// Seed per-step UI state (keep saved values, default the rest): event-level steps by
// plain id + per-witness steps keyed by witness. Every key gets a defined
// { expanded, done } that the autosave persists. Event-level steps (Preparation,
// Attendance) start expanded; per-witness steps start collapsed.
export const getInitialStepsState = (
  witnessCount: number,
  saved?: CrFormStepsState,
): CrFormStepsState => {
  const keys = [
    ...EVENT_LEVEL_STEPS,
    ...Array.from({ length: Math.max(witnessCount, 1) }, (_, i) =>
      PER_WITNESS_STEPS.map(step => witnessStepKey(step, i)),
    ).flat(),
  ]

  return keys.reduce<CrFormStepsState>((acc, id) => {
    acc[id] = saved?.[id] ?? {
      expanded: id === CrFormStep.PREPARATION,
      done: false,
    }
    return acc
  }, {})
}

export type StepStatus = 'unchecked' | 'done' | 'invalid'

// Live status of a step: a step with invalid fields always shows as invalid;
// otherwise it reflects whether the SA has marked it done.
export const deriveStepStatus = ({
  done,
  hasError,
}: {
  done: boolean
  hasError: boolean
}): StepStatus => (hasError ? 'invalid' : done ? 'done' : 'unchecked')

// The minimal slice of the feedback form a Step needs, so steps stay decoupled
// from the full useFeedbackForm bundle.
export type CrFormHandle = Pick<
  UseFormReturnType<ReporterFeedback>,
  'values' | 'errors' | 'setFieldValue' | 'validateField' | 'getInputProps'
>
